import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux'
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import KeplerGl from "@kepler.gl/components";
import { CardComponent } from '../../components/Layouts/Card.styles'
import Typography from '../../components/Basics/Typography'
import { DatePicker, DatePickerInput, ProgressBar } from '@carbon/react'
import { useDatePicker } from '../../components/Inputs/Inputs.hooks'
import { onGetMapboxThunk } from "./Kepler.actions";
import { checkErrorRequired } from "../../modules/errors/validations";
import LoadingContent from '../../components/Animations/LoadingContent'
import keplerConfig from '../../data/cycle-kepler'
import { processCsvData, processGeojson } from '@kepler.gl/processors';
import { addDataToMap } from '@kepler.gl/actions';
import frentes from '../../assets/geocercas/frentes_geocercas.json'
import layer from '../../assets/geocercas/layer.json'
import { patchCsvTimezone } from "./Utils"

const MAPBOX_TOKEN = "pk.eyJ1Ijoiam9zZS12YWxsZWpvczE0MTIiLCJhIjoiY205YWZqajQzMDRwYjJxcTQyZ2dxZGZ1cSJ9.z7BDDJD9HcS6vz_mk-EAsw";

const KeplerMapContainer = () => {
  const today = new Date()
  const dispatch = useDispatch();
  const { loading, error, cycleData, progress, loaded, total } = useSelector(state => state.mapbox);
  
  const hasInitialized = useRef(false);
  const hasProcessedData = useRef(false);
  const lastCycleDataRef = useRef(null);
  
  const progressInMB = (loaded / (1024 * 1024)).toFixed(1);
  const totalInMB = (total / (1024 * 1024)).toFixed(1);
  const helperText = progress > 0 && progress < 100 
    ? `${progressInMB}MB of ${totalInMB}MB`
    : (progress === 100 ? 'Done' : 'Fetching assets...');
  
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  
  const { error: dateError, value: dateValue, onChange: handleDateChange } = useDatePicker({
    initialValue: [yesterday],
    errorCallbacks: [checkErrorRequired()]
  })
  
  const dispatchReport = (date) => {
    handleDateChange(date)
    hasProcessedData.current = false;
    lastCycleDataRef.current = null;
    dispatch(onGetMapboxThunk(date))
  }
  
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      
      dispatch(onGetMapboxThunk([yesterday]));
    }
  }, []);
  
  useEffect(() => {
    if (!cycleData || 
        cycleData.length === 0 || 
        hasProcessedData.current || 
        cycleData === lastCycleDataRef.current) {
      return;
    }
    
    hasProcessedData.current = true;
    lastCycleDataRef.current = cycleData;
    
    try {
      const patchedCsv = patchCsvTimezone(cycleData);
      const data = processCsvData(patchedCsv);
      const geoData = processGeojson(frentes);
      const newLayer = processGeojson(layer);
      
      let timeRange = null;
      if (data.rows && data.rows.length > 0) {
        const timeFieldIndex = data.fields.findIndex(field => 
          field.name === 'event_time'
        );
        
        if (timeFieldIndex !== -1) {
          const chunkSize = 10000;
          let minTime = Infinity;
          let maxTime = -Infinity;
          
          for (let i = 0; i < data.rows.length; i += chunkSize) {
            const chunk = data.rows.slice(i, Math.min(i + chunkSize, data.rows.length));
            
            const chunkTimestamps = chunk
              .map(row => new Date(row[timeFieldIndex]).getTime())
              .filter(t => !isNaN(t));
            
            if (chunkTimestamps.length > 0) {
              minTime = Math.min(minTime, ...chunkTimestamps);
              maxTime = Math.max(maxTime, ...chunkTimestamps);
            }
          }
          
          if (minTime !== Infinity && maxTime !== -Infinity) {
            const timeWindowHours = 0.1;
            const timeWindowMs = timeWindowHours * 60 * 60 * 1000;
            const windowEnd = Math.min(minTime + timeWindowMs, maxTime);
            
            timeRange = [minTime, windowEnd];
          }
        }
      }
      
      const configWithTimeFilter = {
        ...keplerConfig,
        config: {
          ...keplerConfig.config,
          visState: {
            ...keplerConfig.config.visState,
            filters: keplerConfig.config.visState.filters.map(filter => {
              if (filter.id === '13322w6pn' && timeRange) {
                return {
                  ...filter,
                  value: timeRange
                };
              }
              return filter;
            })
          }
        }
      };
      
      const datasets = [
        {
          data: data,
          info: {
            id: 'my_data',
            label: 'GPS Data'
          }
        },
        {
          data: geoData,
          info: {
            id: '-mxgg0v',
            label: 'geocercas'
          }
        },
        {
          data: newLayer,
          info: {
            id: 'newLayer',
            label: 'Label'
          }
        }
      ];
      
      dispatch(addDataToMap({
        datasets,
        config: configWithTimeFilter.config,
        options: {
          centerMap: true,
          readOnly: false
        }
      }));
      
    } catch (err) {
      console.error('Error processing data:', err);
      hasProcessedData.current = false;
      lastCycleDataRef.current = null;
    }
    
  }, [cycleData]);
  
  return (
    <>
      {loading && !error && (
        <>
          <LoadingContent />
          <ProgressBar
            label="Descargando Reporte"
            helperText={helperText}
            value={progress}
            max={100}
            status={progress === 100 ? 'finished' : 'active'}
          />
        </>
      )}
      {!loading && error && (
        <>
          <DatePicker 
            datePickerType="single" 
            maxDate={yesterday.toISOString()} 
            style={{ marginBottom: '40px' }} 
            onChange={dispatchReport} 
            value={dateValue} 
            dateFormat='d-m-Y'
          >
            <DatePickerInput 
              placeholder="dd/mm/yyyy" 
              labelText="Fecha del Reporte" 
              id="daily-datepicker-error" 
              size="md" 
            />
          </DatePicker>
          <CardComponent width="10%">
            <Typography className='cds--type-heading-compact-01'>
              No hay datos para este dia
            </Typography>
          </CardComponent>
        </>
      )}
      {!loading && !error && (
        <>
          <DatePicker 
            datePickerType="single" 
            maxDate={yesterday.toISOString()} 
            style={{ marginBottom: '40px' }} 
            onChange={dispatchReport} 
            value={dateValue} 
            dateFormat='d-m-Y'
          >
            <DatePickerInput 
              placeholder="dd/mm/yyyy" 
              labelText="Fecha del Reporte" 
              id="daily-datepicker-error" 
              size="md" 
            />
          </DatePicker>
          <div style={{ height: "93vh", width: "96vw" }}>
            <AutoSizer>
              {({ height, width }) => (
                <KeplerGl
                  id="map"
                  mapboxApiAccessToken={MAPBOX_TOKEN}
                  width={width}
                  height={height}
                />
              )}
            </AutoSizer>
          </div>
        </>
      )}
    </>
  );
};

export default KeplerMapContainer;